import React, { useCallback, useMemo, useRef, useState } from 'react';
import Map, { Layer, Source, NavigationControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

const clusterLayer = {
  id: 'clusters',
  type: 'circle',
  filter: ['has', 'point_count'],
  paint: {
    'circle-color': ['step', ['get', 'point_count'], '#b8c5ed', 5, '#95acd9', 20, '#d8e3fa'],
    'circle-radius': ['step', ['get', 'point_count'], 18, 5, 24, 20, 32],
    'circle-stroke-width': 2,
    'circle-stroke-color': '#1a1a1a',
  },
};

const clusterCountLayer = {
  id: 'cluster-count',
  type: 'symbol',
  filter: ['has', 'point_count'],
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 12,
  },
  paint: { 'text-color': '#1a1a1a' },
};

const unclusteredPointLayer = {
  id: 'unclustered-point',
  type: 'circle',
  filter: ['!', ['has', 'point_count']],
  paint: {
    'circle-color': '#b8c5ed',
    'circle-radius': 8,
    'circle-stroke-width': 2,
    'circle-stroke-color': '#fff',
  },
};

function threadsToGeoJSON(threads) {
  return {
    type: 'FeatureCollection',
    features: threads.map((t) => ({
      type: 'Feature',
      properties: {
        thread_id: t.id,
        title: t.title || t.body?.slice(0, 40),
        kind: t.kind,
        up_count: t.up_count ?? 0,
        down_count: t.down_count ?? 0,
        board: t.board?.name || '',
        tags: (t.tags || []).join(','),
        gps_verified: t.gps_verified === 1,
      },
      geometry: {
        type: 'Point',
        coordinates: [t.longitude, t.latitude],
      },
    })),
  };
}

export default function CommunityMap({
  threads = [],
  onSelectThreads,
  initialViewState,
  onMoveEnd,
}) {
  const mapRef = useRef(null);
  const [viewState, setViewState] = useState(
    initialViewState || {
      longitude: 127.0276,
      latitude: 37.4979,
      zoom: 10,
    },
  );

  const geojson = useMemo(() => threadsToGeoJSON(threads), [threads]);

  const handleMapClick = useCallback(
    (event) => {
      const map = mapRef.current?.getMap?.();
      if (!map) return;

      const features = map.queryRenderedFeatures(event.point, {
        layers: ['clusters', 'unclustered-point'],
      });
      if (!features.length) return;

      const feature = features[0];
      const clusterId = feature.properties?.cluster_id;

      if (clusterId != null) {
        const source = map.getSource('threads');
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          map.easeTo({
            center: feature.geometry.coordinates,
            zoom,
          });
        });
        const clusterThreads = threads.filter((t) => {
          const coords = [t.longitude, t.latitude];
          const bounds = map.getBounds();
          return bounds.contains(coords);
        });
        onSelectThreads?.(clusterThreads.length ? clusterThreads : threads.slice(0, 20));
      } else {
        const tid = feature.properties?.thread_id;
        const one = threads.filter((t) => t.id === tid);
        onSelectThreads?.(one.length ? one : threads);
      }
    },
    [threads, onSelectThreads],
  );

  if (!MAPBOX_TOKEN) {
    return (
      <div className="flex items-center justify-center h-full bg-dark-surface border border-dark-border rounded-lg p-6 text-center">
        <p className="text-dark-text-secondary text-sm max-w-md">
          Set <code className="text-primary">REACT_APP_MAPBOX_ACCESS_TOKEN</code> in .env to enable the map.
          Thread list below still works from mock/API data.
        </p>
      </div>
    );
  }

  return (
    <Map
      ref={mapRef}
      mapboxAccessToken={MAPBOX_TOKEN}
      {...viewState}
      onMove={(evt) => {
        setViewState(evt.viewState);
        onMoveEnd?.(evt.viewState);
      }}
      onClick={handleMapClick}
      mapStyle="mapbox://styles/mapbox/dark-v11"
      style={{ width: '100%', height: '100%' }}
      interactiveLayerIds={['clusters', 'unclustered-point']}
    >
      <NavigationControl position="top-right" />
      <Source
        id="threads"
        type="geojson"
        data={geojson}
        cluster
        clusterMaxZoom={14}
        clusterRadius={50}
      >
        <Layer {...clusterLayer} />
        <Layer {...clusterCountLayer} />
        <Layer {...unclusteredPointLayer} />
      </Source>
    </Map>
  );
}
